/**
 * 盤面の左上に出す「✨ 豆知識」ボタン。
 *
 * 以前は背景の光るマーカーを直接タップして豆知識を開いていたが、マーカーが料理を落とす
 * 高さにあるため、落とすつもりのタップでカードが開いてゲームが止まっていた。
 * 盤面のタップは「落とす」専用にし、豆知識はこのボタンから開く。
 *
 * いま映っている景色にまだ開いていない豆知識があれば、光って数を出す。
 */

import { GameState } from '../core/GameState';
import type { GameContext } from '../game/GameContext';
import type { TourHotspot, TourScene } from '../game/config/types';
import { startupNotices } from './noticeQueue';

export class TourChip {
  private readonly button: HTMLButtonElement;
  private readonly hint: HTMLElement;
  private hintShown = false;
  private scene: TourScene | null = null;

  constructor(
    private readonly context: GameContext,
    root: ParentNode = document,
  ) {
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'tour-chip';
    this.button.hidden = true;
    this.button.addEventListener('click', () => this.open());
    root.querySelector('.stage-frame')?.append(this.button);

    // はじめての人にだけ、ボタンの下に一度だけ吹き出しを出す
    this.hint = document.createElement('div');
    this.hint.className = 'tour-chip-hint';
    this.hint.textContent = '背景の景色の豆知識は、ここから読めます';
    this.hint.hidden = true;
    this.hint.setAttribute('role', 'status');
    root.querySelector('.stage-frame')?.append(this.hint);
    context.state.onChange((to) => {
      if (to === GameState.PLAYING) this.maybeShowHint();
    });

    context.bus.on('tour:scene', ({ scene }) => {
      this.scene = scene;
      this.render();
    });
    context.bus.on('tour:discover', () => this.render());
  }

  private maybeShowHint(): void {
    if (this.hintShown || this.button.hidden) return;
    if (this.context.storage.discoveredTipIds.length > 0) return;
    this.hintShown = true;
    // 「ようこそ」のお知らせと同時に出さない（遊び始めのお知らせは1つずつ順番に）
    window.setTimeout(() => {
      startupNotices.push({
        durationMs: 5000,
        stillWanted: () =>
          this.context.state.is(GameState.PLAYING) && this.context.storage.discoveredTipIds.length === 0,
        show: () => {
          this.hint.hidden = false;
        },
        hide: () => {
          this.hint.hidden = true;
        },
      });
    }, 1500);
  }

  private undiscovered(): TourHotspot[] {
    if (!this.scene) return [];
    const found = new Set(this.context.storage.discoveredTipIds);
    return this.scene.hotspots.filter((h) => !found.has(h.id));
  }

  private open(): void {
    this.hint.hidden = true;
    if (!this.scene || this.scene.hotspots.length === 0) return;
    // まだ開いていないものを先に。全部開いていれば、この景色の1つ目をもう一度
    const hotspot = this.undiscovered()[0] ?? this.scene.hotspots[0];
    const playing = this.context.state.is(GameState.PLAYING);
    if (playing) this.context.bus.emit('command:pause');
    this.context.bus.emit('tour:openTip', { hotspot, resumeOnClose: playing });
  }

  private render(): void {
    const scene = this.scene;
    if (!scene || scene.hotspots.length === 0) {
      this.button.hidden = true;
      return;
    }
    const rest = this.undiscovered().length;
    this.button.hidden = false;
    this.button.dataset.fresh = String(rest > 0);
    this.button.textContent = rest > 0 ? `✨ 豆知識 ${rest}` : '✨ 豆知識';
    this.button.setAttribute(
      'aria-label',
      rest > 0
        ? `${scene.name}の豆知識をひらく（まだ${rest}つ）`
        : `${scene.name}の豆知識をもう一度ひらく`,
    );
  }
}
